import { useState, useEffect, useCallback, useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

const MAGNETIC_RADIUS = 60;

export default function MagneticCursor() {
  const cursorX = useMotionValue(-100);
  const cursorY = useMotionValue(-100);
  const dotX = useSpring(cursorX, { stiffness: 500, damping: 28 });
  const dotY = useSpring(cursorY, { stiffness: 500, damping: 28 });
  const ringX = useSpring(cursorX, { stiffness: 180, damping: 22 });
  const ringY = useSpring(cursorY, { stiffness: 180, damping: 22 });
  const ringScale = useSpring(1, { stiffness: 300, damping: 20 });
  const [hidden, setHidden] = useState(false);
  const magneticTarget = useRef(null);

  const handleMouseMove = useCallback(
    (e) => {
      let x = e.clientX;
      let y = e.clientY;

      // Magnetic pull toward interactive elements
      const interactives = document.querySelectorAll(
        'a, button, [role="button"], [data-magnetic]'
      );
      let snapped = false;

      for (const el of interactives) {
        const rect = el.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dist = Math.hypot(x - cx, y - cy);

        if (dist < MAGNETIC_RADIUS) {
          const pull = 1 - dist / MAGNETIC_RADIUS;
          x += (cx - x) * pull * 0.35;
          y += (cy - y) * pull * 0.35;
          ringScale.set(1.6);
          magneticTarget.current = el;
          snapped = true;
          break;
        }
      }

      if (!snapped) {
        ringScale.set(1);
        magneticTarget.current = null;
      }

      cursorX.set(x);
      cursorY.set(y);
    },
    [cursorX, cursorY, ringScale]
  );

  useEffect(() => {
    // Respect reduced motion
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;

    // Hide on touch devices
    const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) {
      setHidden(true);
      return;
    }

    window.addEventListener("mousemove", handleMouseMove);
    const handleLeave = () => setHidden(true);
    const handleEnter = () => setHidden(false);
    document.addEventListener("mouseleave", handleLeave);
    document.addEventListener("mouseenter", handleEnter);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseleave", handleLeave);
      document.removeEventListener("mouseenter", handleEnter);
    };
  }, [handleMouseMove]);

  if (hidden) return null;

  return (
    <>
      {/* Dot */}
      <motion.div
        className="fixed top-0 left-0 z-[9999] pointer-events-none mix-blend-difference"
        style={{
          x: dotX,
          y: dotY,
          translateX: "-50%",
          translateY: "-50%",
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: "#fff",
        }}
      />
      {/* Ring */}
      <motion.div
        className="fixed top-0 left-0 z-[9998] pointer-events-none"
        style={{
          x: ringX,
          y: ringY,
          translateX: "-50%",
          translateY: "-50%",
          width: 36,
          height: 36,
          borderRadius: "50%",
          border: "1px solid rgba(99, 102, 241, 0.5)",
          scale: ringScale,
        }}
      />
    </>
  );
}
